import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  AppBar,
  Toolbar,
  Avatar,
  styled,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  OutlinedInput
} from '@mui/material';
import api from '../../services/api';
import {
  QrCode,
  Download,
  Refresh,
  CheckCircle,
  AccessTime,
  History,
  Info,
  School,
  Person,
  Event,
  AccessTime as TimeIcon,
  Timer,
  ContentCopy,
  Notifications,
  Print,
  Search,
  FilterList,
  CalendarToday,
  FileDownload
} from '@mui/icons-material';
import { QRCodeCanvas } from 'qrcode.react';
import { saveAs } from 'file-saver';

// Styled components for better UI
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
  },
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.85rem',
  padding: '4px 8px',
  borderRadius: 20,
}));

// Printable flyer component
const PrintableFlyer = styled('div')(({ theme }) => ({
  '@media print': {
    '@page': {
      margin: '0.25in',
      size: 'A4',
    },
    body: {
      margin: 0,
      padding: 0,
    },
  },
  fontFamily: '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  maxWidth: '800px',
  margin: '0 auto',
  padding: '20px',
  border: '2px dashed #27ae60',
  borderRadius: '10px',
  backgroundColor: '#ffffff',
  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
  textAlign: 'center',
  '& h1': {
    color: '#27ae60',
    marginBottom: '5px',
    fontSize: '2rem',
  },
  '& h2': {
    color: '#2c3e50',
    marginBottom: '10px',
    fontSize: '1.4rem',
  },
  '& p': {
    fontSize: '0.9rem',
    color: '#34495e',
    marginBottom: '8px',
  },
  '& .qr-container': {
    margin: '10px 0',
    padding: '10px',
    backgroundColor: '#f8f9fa',
    borderRadius: '5px',
    display: 'inline-block',
  },
  '& .token-display': {
    backgroundColor: '#e8f5e9',
    padding: '8px',
    borderRadius: '5px',
    margin: '10px 0',
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#27ae60',
    border: '1px solid #27ae60',
  },
  '& .expiry-info': {
    backgroundColor: '#fff3cd',
    padding: '8px',
    borderRadius: '5px',
    margin: '10px 0',
    fontSize: '0.9rem',
    color: '#856404',
    border: '1px solid #ffc107',
  },
  '& .instructions': {
    textAlign: 'left',
    backgroundColor: '#f0f4f8',
    padding: '10px',
    borderRadius: '5px',
    margin: '10px 0',
  },
  '& .instructions h3': {
    color: '#2c3e50',
    marginBottom: '5px',
    fontSize: '1rem',
  },
  '& .instructions ol': {
    paddingLeft: '15px',
    margin: '5px 0',
  },
  '& .instructions li': {
    marginBottom: '3px',
    fontSize: '0.85rem',
  },
}));

const TeacherAttendancePage: React.FC = () => {
  const [teachers, setTeachers] = useState<any[]>([]);
  const [allTeachers, setAllTeachers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeacher, setSelectedTeacher] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [attendanceRecords, setAttendanceRecords] = useState<any[]>([]);
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [qrToken, setQrToken] = useState<string>('');
  const [tokenExpiry, setTokenExpiry] = useState<Date | null>(null);
  const [tokenDuration, setTokenDuration] = useState<number>(30); // minutes
  const [printFlyerOpen, setPrintFlyerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState<any[]>([]);
  const [teacherAttendanceData, setTeacherAttendanceData] = useState<any[]>([]);

  // Log when component mounts
  useEffect(() => {
    console.log('TeacherAttendancePage mounted');
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    console.log('Token present:', !!token);
    console.log('User present:', !!user);
    if (user) {
      try {
        const userData = JSON.parse(user);
        console.log('User role:', userData.role);
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
    // Set debug flag to prevent auto-logout
    localStorage.setItem('preventAutoLogout', 'true');
  }, []);

  // Fetch all teachers from backend
  const fetchAllTeachers = async () => {
    try {
      // Log the token for debugging
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      console.log('Fetching teachers with token:', token ? 'Token present' : 'No token');
      
      // Check user role
      if (user) {
        try {
          const userData = JSON.parse(user);
          console.log('Current user role:', userData.role);
          if (userData.role !== 'admin' && userData.role !== 'super_admin') {
            console.warn('User does not have required role for teachers endpoint');
          }
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }
      
      const response = await api.get('/users/teachers');
      console.log('Teachers API response:', response);
      const teachersData = response.data.data?.teachers || [];
      
      // Transform teachers data to match expected format
      const transformedTeachers = teachersData.map((teacher: any) => ({
        id: teacher._id,
        name: `${teacher.firstName} ${teacher.lastName}`,
        email: teacher.email,
        department: teacher.specialization || 'General'
      }));
      
      setAllTeachers(transformedTeachers);
      setTeachers(transformedTeachers);
    } catch (error) {
      console.error('Error fetching teachers:', error);
      // No fallback to sample data - show empty state instead
      setAllTeachers([]);
      setTeachers([]);
    }
  };

  // Fetch attendance records from backend
  const fetchAttendanceRecords = async () => {
    try {
      setLoading(true);
      const response = await api.get('/teacher-attendance');
      const records = response.data;
      setAttendanceRecords(records);
      
      // Process records to get unique teachers who have attendance
      const teachersWithAttendance = Array.from(
        new Set(records.map((record: any) => record.teacherName))
      ).map((name: string) => {
        // Ensure name is a string before calling string methods
        const teacherName = typeof name === 'string' ? name : String(name);
        return {
          name: teacherName,
          id: teacherName.replace(/\s+/g, '_'),
          email: `${teacherName.replace(/\s+/g, '.').toLowerCase()}@example.com`,
          department: 'General'
        };
      });
      
      setTeachers(teachersWithAttendance);
    } catch (error) {
      console.error('Error fetching attendance records:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch specific teacher's attendance records
  const fetchTeacherAttendanceRecords = async (teacherName: string) => {
    try {
      const response = await api.get(`/teacher-attendance/teacher/${teacherName}`);
      const records = response.data;
      setTeacherAttendanceData(records);
      return records;
    } catch (error) {
      console.error('Error fetching teacher attendance records:', error);
      return [];
    }
  };

  // Generate a new time-limited token
  const generateNewToken = async () => {
    try {
      // Try the direct endpoint first for testing
      const response = await api.post('/teacher-attendance/create-token/direct', { duration: tokenDuration });
      
      const result = response.data;
      
      console.log('Token generated:', result.token);
      // Validate that we received a proper token
      if (typeof result.token === 'string' && result.token.length > 0) {
        setQrToken(result.token);
        setTokenExpiry(new Date(result.expiry));
        console.log('QR Code URL will be:', `https://echattendance.onrender.com?token=${encodeURIComponent(result.token)}`);
      } else {
        console.error('Invalid token received from server:', result);
      }
    } catch (error) {
      console.error('Error generating token:', error);
      // Try the regular endpoint as fallback
      try {
        const response = await api.post('/teacher-attendance/create-token', { duration: tokenDuration });
        const result = response.data;
        console.log('Token generated (fallback):', result.token);
        if (typeof result.token === 'string' && result.token.length > 0) {
          setQrToken(result.token);
          setTokenExpiry(new Date(result.expiry));
          console.log('QR Code URL will be:', `https://echattendance.onrender.com?token=${encodeURIComponent(result.token)}`);
        }
      } catch (fallbackError) {
        console.error('Error generating token (fallback):', fallbackError);
      }
    }
  };

  // Fetch attendance records on component mount and generate initial token
  useEffect(() => {
    console.log('Component mounted, fetching initial data');
    // Log current auth state
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    console.log('Fetching data with token:', !!token, 'user:', !!user);
    fetchAllTeachers();
    fetchAttendanceRecords();
    generateNewToken();
  }, []);

  // Generate QR code value with token
  const generateQRCodeValue = () => {
    if (!qrToken) {
      console.log('No token available, returning base URL');
      return 'https://echattendance.onrender.com';
    }
    // Ensure the token is properly encoded
    const encodedToken = encodeURIComponent(qrToken);
    const url = `https://echattendance.onrender.com?token=${encodedToken}`;
    console.log('Generating QR Code with URL:', url);
    return url;
  };

  const handleDownloadQR = () => {
    const canvas = document.getElementById('qr-code') as HTMLCanvasElement;
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = url;
      link.download = 'teacher-attendance-qr.png';
      link.click();
    }
  };

  const handlePrintFlyer = () => {
    setPrintFlyerOpen(true);
  };

  const handleCopyToken = () => {
    navigator.clipboard.writeText(qrToken);
  };

  const handleViewHistory = async (teacher: any) => {
    setSelectedTeacher(teacher);
    setDialogOpen(true);
    
    // Fetch specific teacher's attendance records
    const records = await fetchTeacherAttendanceRecords(teacher.name);
    setTeacherAttendanceData(records);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTeacher(null);
    setTeacherAttendanceData([]);
  };

  const handleClosePrintFlyer = () => {
    setPrintFlyerOpen(false);
  };

  const handlePrint = () => {
    window.print();
  };

  // Filter teachers based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = allTeachers.filter(teacher => {
        // Ensure teacher.name and teacher.department are strings before calling string methods
        const teacherName = typeof teacher.name === 'string' ? teacher.name : String(teacher.name);
        const teacherDepartment = typeof teacher.department === 'string' ? teacher.department : String(teacher.department);
        return teacherName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          teacherDepartment.toLowerCase().includes(searchTerm.toLowerCase());
      });
      setTeachers(filtered);
    } else {
      setTeachers(allTeachers);
    }
  }, [searchTerm, allTeachers]);

  // Debug QR code value changes
  useEffect(() => {
    console.log('QR Token updated:', qrToken);
    console.log('QR Code URL:', generateQRCodeValue());
  }, [qrToken]);

  // Apply date filters
  const applyDateFilters = async () => {
    if (!startDate || !endDate) return;
    
    try {
      const response = await api.get(`/teacher-attendance/range?startDate=${startDate}&endDate=${endDate}`);
      const records = response.data;
      setAttendanceRecords(records);
      
      // Update teachers list based on filtered records
      const teachersWithAttendance = Array.from(
        new Set(records.map((record: any) => record.teacherName))
      ).map((name: string) => {
        // Ensure name is a string before calling string methods
        const teacherName = typeof name === 'string' ? name : String(name);
        return {
          name: teacherName,
          id: teacherName.replace(/\s+/g, '_'),
          email: `${teacherName.replace(/\s+/g, '.').toLowerCase()}@example.com`,
          department: 'General'
        };
      });
      
      setTeachers(teachersWithAttendance);
    } catch (error) {
      console.error('Error fetching filtered attendance records:', error);
    }
  };

  // Reset filters
  const resetFilters = () => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    fetchAttendanceRecords();
  };

  // Export attendance data to CSV
  const exportToCSV = (records: any[], filename: string) => {
    const headers = ['Teacher Name', 'Date', 'Action', 'Time'];
    const csvContent = [
      headers.join(','),
      ...records.map(record => [
        typeof record.teacherName === 'string' ? record.teacherName : String(record.teacherName),
        new Date(record.date).toLocaleDateString(),
        record.status === 'missed' ? 'Missed' : (record.startTime && !record.endTime ? 'Start' : 'End'),
        record.startTime && !record.endTime ? 
          new Date(record.startTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 
          (record.endTime ? new Date(record.endTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 
          (record.status === 'missed' ? 'Not completed' : '-'))
      ].map(field => `"${field}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, filename);
  };

  // Export selected teacher's attendance
  const exportTeacherAttendance = () => {
    if (selectedTeacher && teacherAttendanceData.length > 0) {
      // Ensure selectedTeacher.name is a string before calling replace
      const teacherName = typeof selectedTeacher.name === 'string' ? selectedTeacher.name : String(selectedTeacher.name);
      exportToCSV(teacherAttendanceData, `attendance_${teacherName.replace(/\s+/g, '_')}.csv`);
    }
  };

  // Export all attendance records
  const exportAllAttendance = () => {
    if (attendanceRecords.length > 0) {
      exportToCSV(attendanceRecords, 'all_teacher_attendance.csv');
    }
  };

  // Calculate working hours for a record
  const calculateWorkingHours = (record: any) => {
    if (record.duration) {
      const hours = Math.floor(record.duration / 60);
      const minutes = record.duration % 60;
      return `${hours}h ${minutes}m`;
    }
    return '-';
  };

  // Format expiry time
  const formatExpiryTime = () => {
    if (!tokenExpiry) return 'Not set';
    return tokenExpiry.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Format expiry date
  const formatExpiryDate = () => {
    if (!tokenExpiry) return 'Not set';
    return tokenExpiry.toLocaleDateString();
  };

  // Handle record selection for export
  const handleRecordSelect = (recordId: string, checked: boolean) => {
    if (checked) {
      setSelectedRecords([...selectedRecords, recordId]);
    } else {
      setSelectedRecords(selectedRecords.filter(id => id !== recordId));
    }
  };

  // Trigger missed attendance check
  const triggerMissedAttendanceCheck = async () => {
    try {
      const response = await api.post('/teacher-attendance/trigger-missed-check');
      
      const result = response.data;
      
      if (result.success) {
        // Refresh the attendance records to show any newly marked missed records
        fetchAttendanceRecords();
      } else {
        console.error('Error triggering missed attendance check:', result.message);
      }
    } catch (error) {
      console.error('Error triggering missed attendance check:', error);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <AppBar position="static" sx={{ borderRadius: 2, mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Toolbar>
          <QrCode sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Teacher Attendance Dashboard
          </Typography>
          <IconButton 
            color="inherit" 
            onClick={fetchAttendanceRecords}
            sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' }
            }}
          >
            <Refresh />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Avatar sx={{ 
          width: 80, 
          height: 80, 
          margin: '0 auto 20px', 
          bgcolor: 'primary.main',
          boxShadow: 3
        }}>
          <School sx={{ fontSize: 40 }} />
        </Avatar>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
          Teacher Attendance System
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
          Monitor and manage teacher attendance with QR code scanning
        </Typography>
      </Box>

      <>
      <Alert 
        severity="info" 
        sx={{ 
          mb: 4, 
          borderRadius: 3,
          boxShadow: 2,
          '& .MuiAlert-message': { width: '100%' }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Info sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>How It Works</Typography>
        </Box>
        <Typography variant="body1" sx={{ mb: 2 }}>
          <strong>Simple Process:</strong> Teachers scan the QR code when they start work and scan again when they end work. 
          The system automatically tracks their working hours.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AccessTime sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="body2">
              Teachers access: <Link href="https://echattendance.onrender.com" target="_blank" sx={{ fontWeight: 600 }}>https://echattendance.onrender.com</Link>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Timer sx={{ mr: 1, color: 'secondary.main' }} />
            <Typography variant="body2">
              Real-time tracking
            </Typography>
          </Box>
        </Box>
      </Alert>
      
      {/* Show alert if there are missed attendance records */}
      {attendanceRecords.some(record => record.status === 'missed') && (
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 4, 
            borderRadius: 3,
            boxShadow: 2,
            '& .MuiAlert-message': { width: '100%' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Notifications sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Missed Attendance Records</Typography>
          </Box>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Attention:</strong> Some teachers have started their attendance but did not complete it within 24 hours. 
            These records are marked as "Missed" in the table below.
          </Typography>
          <Typography variant="body2">
            Please review these records and follow up with the respective teachers.
          </Typography>
        </Alert>
      )}
      </>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} thickness={4} />
        </Box>
      ) : (
        <Grid container spacing={4}>
          <Grid size={{ xs: 12 }}>
            <StyledCard>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Person sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Teachers Overview</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="Search teachers..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ minWidth: 200 }}
                    />
                    
                    <Button
                      startIcon={<FilterList />}
                      onClick={() => setShowFilters(!showFilters)}
                      variant={showFilters ? "contained" : "outlined"}
                      size="small"
                    >
                      Filters
                    </Button>
                    
                    <IconButton 
                      onClick={fetchAttendanceRecords}
                      sx={{ 
                        backgroundColor: 'grey.100',
                        '&:hover': { backgroundColor: 'grey.200' }
                      }}
                    >
                      <Refresh />
                    </IconButton>
                    
                    <Button
                      startIcon={<FileDownload />}
                      onClick={exportAllAttendance}
                      variant="outlined"
                      size="small"
                      disabled={attendanceRecords.length === 0}
                    >
                      Export All
                    </Button>
                  </Box>
                </Box>
                
                {showFilters && (
                  <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Filter by Date Range</Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                      <TextField
                        label="Start Date"
                        type="date"
                        size="small"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        label="End Date"
                        type="date"
                        size="small"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                      <Button
                        variant="contained"
                        onClick={applyDateFilters}
                        startIcon={<FilterList />}
                        disabled={!startDate || !endDate}
                      >
                        Apply Filters
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={resetFilters}
                      >
                        Reset
                      </Button>
                    </Box>
                  </Box>
                )}
                
                <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Teacher</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Today's Action</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {teachers.map((teacher) => {
                        const todayRecord = attendanceRecords.find(
                          record => record.teacherName === teacher.name && 
                          (record.date instanceof Date ? record.date : new Date(record.date)).toISOString().split('T')[0] === date
                        );
                        
                        return (
                          <TableRow 
                            key={teacher.id} 
                            sx={{ 
                              '&:hover': { backgroundColor: 'grey.50' },
                              transition: 'background-color 0.2s'
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar sx={{ width: 32, height: 32, mr: 2, bgcolor: 'primary.main' }}>
                                  {/* Ensure teacher.name is a string before calling charAt */}
                                  {typeof teacher.name === 'string' ? teacher.name.charAt(0) : String(teacher.name).charAt(0)}
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>{typeof teacher.name === 'string' ? teacher.name : String(teacher.name)}</Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {todayRecord ? (
                                todayRecord.status === 'missed' ? (
                                  <StatusChip 
                                    label="Missed" 
                                    color="error" 
                                    size="small" 
                                  />
                                ) : todayRecord.startTime && !todayRecord.endTime ? (
                                  <StatusChip 
                                    label="Start" 
                                    color="warning" 
                                    size="small" 
                                  />
                                ) : (
                                  <StatusChip 
                                    label="End" 
                                    color="success" 
                                    size="small" 
                                  />
                                )
                              ) : (
                                <StatusChip label="Not Started" color="default" size="small" />
                              )}
                            </TableCell>
                            <TableCell>
                              {todayRecord ? (
                                todayRecord.startTime && !todayRecord.endTime ? 
                                  new Date(todayRecord.startTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 
                                  (todayRecord.endTime ? new Date(todayRecord.endTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-')
                              ) : '-'}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="View History">
                                <IconButton 
                                  onClick={() => handleViewHistory(teacher)}
                                  sx={{ 
                                    color: 'primary.main',
                                    '&:hover': { backgroundColor: 'primary.light', color: 'primary.contrastText' }
                                  }}
                                >
                                  <History />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </StyledCard>
          </Grid>

          <Grid size={{ xs: 12, lg: 4 }}>
            <StyledCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <QrCode sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Attendance QR Code</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                  Scan this QR code to mark attendance
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'white', 
                    borderRadius: 3, 
                    boxShadow: 2,
                    display: 'inline-block'
                  }}>
                    {qrToken ? (
                      <QRCodeCanvas
                        id="qr-code"
                        value={generateQRCodeValue()}
                        size={180}
                        level="H"
                        includeMargin={true}
                      />
                    ) : (
                      <Box sx={{ width: 180, height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CircularProgress size={40} />
                      </Box>
                    )}
                  </Box>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Token: {qrToken ? `${qrToken.substring(0, 10)}...` : 'Generating...'}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={handleCopyToken}
                      sx={{ 
                        backgroundColor: 'grey.100',
                        '&:hover': { backgroundColor: 'grey.200' }
                      }}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Notifications sx={{ mr: 1, color: 'warning.main' }} />
                    <Typography variant="body2" color="text.secondary">
                      Expires at: {formatExpiryTime()} on {formatExpiryDate()}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Duration (minutes)</InputLabel>
                      <Select
                        value={tokenDuration}
                        label="Duration (minutes)"
                        onChange={(e) => setTokenDuration(Number(e.target.value))}
                      >
                        <MenuItem value={15}>15 minutes</MenuItem>
                        <MenuItem value={30}>30 minutes</MenuItem>
                        <MenuItem value={60}>60 minutes</MenuItem>
                        <MenuItem value={120}>2 hours</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
                
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => generateNewToken()}
                  size="large"
                  sx={{ 
                    py: 1.5,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    fontWeight: 600,
                    boxShadow: 3,
                    mb: 2,
                    '&:hover': {
                      boxShadow: 5,
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  🔄 Generate New Token
                </Button>
                
                <Button
                  fullWidth
                  variant="contained"
                  onClick={triggerMissedAttendanceCheck}
                  size="large"
                  sx={{ 
                    py: 1.5,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                    fontWeight: 600,
                    boxShadow: 3,
                    mb: 2,
                    '&:hover': {
                      boxShadow: 5,
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  🔍 Check Missed Attendance
                </Button>
                
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Download />}
                    onClick={handleDownloadQR}
                    size="large"
                    sx={{ 
                      py: 1.5,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)',
                      fontWeight: 600,
                      boxShadow: 3,
                      '&:hover': {
                        boxShadow: 5,
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    Download QR
                  </Button>
                  
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Print />}
                    onClick={handlePrintFlyer}
                    size="large"
                    sx={{ 
                      py: 1.5,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                      fontWeight: 600,
                      boxShadow: 3,
                      '&:hover': {
                        boxShadow: 5,
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    Print Flyer
                  </Button>
                </Box>
                
                <StyledCard sx={{ mt: 3, backgroundColor: '#f0f4f8' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Info sx={{ mr: 1, color: 'info.main' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>Instructions</Typography>
                    </Box>
                    <Box component="ol" sx={{ pl: 2, mb: 0 }}>
                      <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                        Display this QR code in an accessible location
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                        Teachers scan the QR code when starting work
                      </Typography>
                      <Typography component="li" variant="body2">
                        Teachers scan the same QR code when ending work
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                        Token expires after {tokenDuration} minutes - generate a new one when needed
                      </Typography>
                    </Box>
                  </CardContent>
                </StyledCard>
              </CardContent>
            </StyledCard>
          </Grid>

          {/* Attendance History Dialog */}
          <Dialog 
            open={dialogOpen} 
            onClose={handleCloseDialog} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
              sx: { borderRadius: 3 }
            }}
          >
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <History sx={{ mr: 1 }} />
                  Attendance History for {selectedTeacher?.name}
                </Box>
                <Button
                  startIcon={<FileDownload />}
                  onClick={exportTeacherAttendance}
                  variant="outlined"
                  size="small"
                  disabled={teacherAttendanceData.length === 0}
                >
                  Export
                </Button>
              </Box>
            </DialogTitle>
            <DialogContent>
              {teacherAttendanceData.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'grey.50' }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={selectedRecords.length > 0 && selectedRecords.length < teacherAttendanceData.length}
                            checked={teacherAttendanceData.length > 0 && selectedRecords.length === teacherAttendanceData.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedRecords(teacherAttendanceData.map(record => record._id || record.id));
                              } else {
                                setSelectedRecords([]);
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}><Event sx={{ mr: 1, verticalAlign: 'middle' }} /> Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}><Person sx={{ mr: 1, verticalAlign: 'middle' }} /> Teacher Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}><Timer sx={{ mr: 1, verticalAlign: 'middle' }} /> Action</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}><TimeIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {teacherAttendanceData.map((record) => (
                        <TableRow 
                          key={record._id || record.id}
                          sx={{ 
                            '&:hover': { backgroundColor: 'grey.50' },
                            transition: 'background-color 0.2s'
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedRecords.includes(record._id || record.id)}
                              onChange={(e) => handleRecordSelect(record._id || record.id, e.target.checked)}
                            />
                          </TableCell>
                          <TableCell>{new Date(record.date).toLocaleDateString()}</TableCell>
                          <TableCell>{typeof record.teacherName === 'string' ? record.teacherName : String(record.teacherName)}</TableCell>
                          <TableCell>
                            <StatusChip 
                              label={record.status === 'missed' ? 'Missed' : (record.startTime && !record.endTime ? 'Start' : 'End')} 
                              color={record.status === 'missed' ? 'error' : (record.startTime && !record.endTime ? 'warning' : 'success')} 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell>
                            {record.startTime && !record.endTime ? 
                              new Date(record.startTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 
                              (record.endTime ? new Date(record.endTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No attendance records found for this teacher.
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button 
                onClick={handleCloseDialog}
                variant="contained"
                sx={{ 
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  fontWeight: 600
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
          
          {/* Printable Flyer Dialog */}
          <Dialog 
            open={printFlyerOpen} 
            onClose={handleClosePrintFlyer} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
              sx: { borderRadius: 3 }
            }}
          >
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Print sx={{ mr: 1 }} />
                Printable Attendance Flyer
              </Box>
            </DialogTitle>
            <DialogContent>
              <PrintableFlyer id="printable-flyer">
                <Typography variant="h1">🎓 Teacher Attendance</Typography>
                <Typography variant="h2">Scan QR Code to Mark Attendance</Typography>
                
                <Box className="qr-container">
                  {qrToken ? (
                    <QRCodeCanvas
                      value={generateQRCodeValue()}
                      size={200}
                      level="H"
                      includeMargin={true}
                    />
                  ) : (
                    <Box sx={{ width: 200, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CircularProgress size={40} />
                    </Box>
                  )}
                </Box>
                
                <Box className="token-display">
                  🔐 Token: {qrToken || 'Generating...'}
                </Box>
                
                <Box className="expiry-info">
                  ⏰ Expires: {formatExpiryTime()} on {formatExpiryDate()}
                </Box>
                
                <Box className="instructions">
                  <Typography variant="h3">📋 Quick Instructions:</Typography>
                  <ol>
                    <li>📱 Scan QR code with your phone camera</li>
                    <li>📝 Enter your name</li>
                    <li>🔘 Select "Start" when beginning work</li>
                    <li>🔘 Select "End" when finishing work</li>
                  </ol>
                </Box>
                
                <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                  Contact admin for technical issues
                </Typography>
              </PrintableFlyer>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button 
                onClick={handleClosePrintFlyer}
                sx={{ 
                  borderRadius: 2,
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handlePrint}
                variant="contained"
                startIcon={<Print />}
                sx={{ 
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                  fontWeight: 600
                }}
              >
                Print Now
              </Button>
            </DialogActions>
          </Dialog>
        </Grid>
      )}
    </Container>
  );
};

export default TeacherAttendancePage;